import { useCallback, useEffect, useMemo, useState } from "react";

import { TimePicker, TimezoneDisplayFormat } from "@blueprintjs/datetime";
import styled from "@emotion/styled";
import { addDays, format, parse } from "date-fns";
import { enUS } from "date-fns/locale";
import { fromZonedTime } from "date-fns-tz";

import { DateInput3 } from "@/components/helpers/ui/blueprint-overrides/DateInput3";
import { TimezoneSelect } from "@/components/helpers/ui/blueprint-overrides/timezoneSelect";
import { Flex } from "@/components/layout/flexbox";
import { BP_NS } from "@/css/constants";
import { formatDate, getBrowserTimezone } from "@/utils/datetime";

const StyledDateInput2 = styled(DateInput3)`
  flex: 1;
  min-width: fit-content;
  border: 1px solid #d3d3d3;
  border-radius: 6px;
  display: flex;
  align-items: center;
  background-color: white;
  min-height: 39px;
  .${BP_NS}-input {
    height: auto;
    padding: 0px 10px;
  }
  &.date-input-custom {
    .${BP_NS}-input {
      padding-left: 36px;
      border: none;
      box-shadow: none;
    }
  }
  .${BP_NS}-input-action {
    display: flex;
    align-items: center;
    bottom: 0;
  }

  &.${BP_NS}-disabled {
    background-color: rgba(206, 217, 224, 0.5);
    border: none;
    cursor: not-allowed;

    .${BP_NS}-input {
      background-color: transparent;
      color: rgba(92, 112, 128, 0.6);
    }

    .${BP_NS}-input-action {
      color: rgba(92, 112, 128, 0.6);
    }
  }

  &.${BP_NS}-datepicker {
    border: 1px solid #e2e2e2;
    box-shadow:
      0 1px 1px rgba(24, 32, 38, 0.1),
      0 2px 6px rgba(24, 32, 38, 0.05);
    padding: 12px;
    border-radius: 6px;

    .${BP_NS}-timepicker {
      border-top: 1px solid #e2e2e2;
      padding-top: 12px;
    }

    .${BP_NS}-datepicker-month-select .${BP_NS}-icon {
      transform: translateX(5px);
    }

    .DayPicker-NavButton {
      padding: 6.667px;
    }

    .${BP_NS}-datepicker-day-wrapper {
      padding: 12px;
    }

    .${BP_NS}-timepicker-input-row {
      height: 40px;

      .${BP_NS}-timepicker-input {
        height: 40px;
        width: 40px;
      }
    }

    .${BP_NS}-timepicker-ampm-select {
      select {
        height: 40px;
        padding: 0 30px 0 12px;
      }

      .${BP_NS}-icon {
        margin-top: 5px;
      }
    }
  }
`;
const DISABLED_COLOR = "rgba(206, 217, 224, 0.5)";

const StyledTimeInput = styled(TimePicker)`
  &.${BP_NS}-timepicker {
    .${BP_NS}-timepicker-input-row {
      border: none;
      box-shadow: none;
      height: 100%;
    }
    .${BP_NS}-timepicker-input {
      width: 24px;
    }
    .${BP_NS}-timepicker-divider-text {
      width: 6px;
    }
    .${BP_NS}-timepicker-ampm-select {
      select {
        border: none;
        box-shadow: none;
        padding: 0;
        &:disabled {
          background-color: transparent;
        }
      }
      .${BP_NS}-icon {
        display: none;
      }
    }
    &.${BP_NS}-disabled {
      .${BP_NS}-timepicker-input-row {
        background-color: transparent;
      }
    }
  }
`;

const StyledTimezoneSelect = styled(TimezoneSelect)`
  &.${BP_NS}-timezone-select {
    .${BP_NS}-button {
      border: none;
      box-shadow: none;
      max-height: 24px;
      min-height: 24px;
      padding: 0px 8px;
      font-size: 12px;
      background-color: #eef0f2;
      &:hover {
        background-color: #eef0f2;
        box-shadow: none;
      }
    }
  }
`;

const DEFAULT_TIME = "09:00:00";

interface DateTimeInputProps {
  value?: string | null;
  onChange: (datetime: string | null) => void;
  formatString?: string;
  placeholder?: string;
  minDateTime?: Date;
  maxDateTime?: Date;
  disabled?: boolean;
  shortcuts?: Array<{
    date: Date;
    label: string;
  }>;
  timezone?: string | null;
  onChangeTimezone?: (timezone: string) => void;
  containerStyles?: React.CSSProperties;
}

export function DateTimeInputV2({
  value,
  onChange,
  formatString = "MMM d, yyyy",
  placeholder = "Choose date",
  minDateTime,
  maxDateTime,
  timezone,
  onChangeTimezone,
  disabled = false,
  shortcuts,
  containerStyles,
}: DateTimeInputProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(DEFAULT_TIME);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    timezone ?? getBrowserTimezone(),
  );
  const [fullDateTime, setFullDateTime] = useState<Date | null>(
    value ? new Date(value) : null,
  );

  const parseValue = (val: string | null | undefined) => {
    if (!val) {
      return {
        date: null,
        time: DEFAULT_TIME,
        timezone,
      };
    }

    const dateTime = new Date(val);

    return {
      date: formatDate(dateTime, "yyyy-MM-dd", selectedTimezone),
      time: formatDate(dateTime, "HH:mm:ss", selectedTimezone),
    };
  };

  const parseDate = useCallback((str: string, format: string) => {
    return parse(str, format, new Date());
  }, []);

  // Handle initial value update
  useEffect(() => {
    const newValues = parseValue(value);
    setSelectedDate(newValues.date);
    setSelectedTime(newValues.time);
    if (value) {
      const date = new Date(value);
      setFullDateTime(date);
    }
  }, [value]);

  // Handle initial timezone update (only for external prop changes)
  useEffect(() => {
    if (timezone && timezone !== selectedTimezone) {
      setSelectedTimezone(timezone);
      if (fullDateTime) {
        setSelectedDate(formatDate(fullDateTime, "yyyy-MM-dd", timezone));
        setSelectedTime(formatDate(fullDateTime, "HH:mm:ss", timezone));
      }
    }
  }, [timezone, selectedTimezone]);

  // Handle timezone change (user interaction only)
  const handleTimezoneChange = (newTimezone: string) => {
    setSelectedTimezone(newTimezone);
    onChangeTimezone?.(newTimezone);
    if (fullDateTime) {
      setSelectedDate(formatDate(fullDateTime, "yyyy-MM-dd", newTimezone));
      setSelectedTime(formatDate(fullDateTime, "HH:mm:ss", newTimezone));
    }
  };

  const handleDateChange = (dateString: string | null) => {
    setSelectedDate(dateString);
    emitDateTimeChange(dateString, selectedTime, selectedTimezone);
  };

  const handleTimeChange = (time: Date) => {
    const zonedDate = fromZonedTime(time, selectedTimezone);
    const timeString = formatDate(zonedDate, "HH:mm:ss", selectedTimezone);
    setSelectedTime(timeString);
    emitDateTimeChange(selectedDate, timeString, selectedTimezone);
  };

  const emitDateTimeChange = (
    dateString: string | null,
    timeString: string | null,
    timezone: string,
  ) => {
    if (!dateString || !timeString) {
      setFullDateTime(null);
      onChange(null);
      return;
    }

    // Create a local date object from the date/time strings
    const dateTimeString = `${dateString}T${timeString}`;

    // Convert to the specified timezone
    const localDate = new Date(dateTimeString);
    const zonedDate = fromZonedTime(localDate, timezone);

    setFullDateTime(zonedDate);
    onChange(zonedDate.toISOString());
  };

  // Memoize date constraints
  const [minDate, maxDate] = useMemo(() => {
    const processDate = (dateTime: Date | undefined, isMax: boolean) => {
      if (!dateTime) return undefined;

      const date = new Date(dateTime);
      if (isMax) {
        addDays(date, 1);
      }
      return date;
    };

    return [processDate(minDateTime, false), processDate(maxDateTime, true)];
  }, [minDateTime, maxDateTime, selectedTimezone]);

  // Memoize initial month
  const initialMonth = useMemo(() => {
    if (value) {
      return new Date(value);
    }
    if (minDate) {
      return new Date(minDate);
    }
    return new Date();
  }, [value, minDate]);

  return (
    <Flex style={{ gap: 12, width: "100%" }} flexWrap="wrap">
      <StyledDateInput2
        // see https://github.com/palantir/blueprint/issues/6481
        dateFnsLocaleLoader={() => Promise.resolve(enUS)}
        className="date-input-custom"
        formatDate={(date) => format(date, formatString) ?? ""}
        onChange={(datetime, isUserChange) => {
          if (isUserChange) {
            handleDateChange(datetime);
          }
        }}
        parseDate={(dateString) => parseDate(dateString, formatString)}
        placeholder={placeholder}
        value={selectedDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultTimezone={selectedTimezone}
        shortcuts={shortcuts}
        highlightCurrentDay={true}
        initialMonth={initialMonth}
        disabled={disabled}
      />
      <Flex
        style={{
          height: 39,
          border: disabled ? "none" : "1px solid #d3d3d3",
          borderRadius: 6,
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0px 4px",
          gap: 8,
          flex: 1,
          minWidth: "fit-content",
          backgroundColor: disabled ? DISABLED_COLOR : "white",
          ...containerStyles,
        }}
      >
        <StyledTimeInput
          disabled={disabled}
          useAmPm={true}
          value={selectedTime ? parseDate(selectedTime, "HH:mm:ss") : null}
          onChange={handleTimeChange}
        />
        <StyledTimezoneSelect
          disabled={disabled}
          onChange={handleTimezoneChange}
          value={selectedTimezone}
          valueDisplayFormat={TimezoneDisplayFormat.ABBREVIATION}
        />
      </Flex>
    </Flex>
  );
}
